import React, { useEffect, useState, useRef } from 'react';
import { axiosAuth } from '../contexts/AuthContext';
import {
  Activity, Users, Eye, DollarSign, TrendingUp, TrendingDown,
  Music, Radio, BookOpen, Download, Printer, RefreshCw
} from 'lucide-react';
import logger from '../utils/logger';

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtNumber = (n) => Number(n || 0).toLocaleString();
const fmtPct = (p) => {
  if (p == null) return '—';
  const sign = p > 0 ? '+' : '';
  return `${sign}${p}%`;
};

const KpiCard = ({ icon: Icon, label, value, sub, accent = '#D4AF37', testid }) => (
  <div
    className="bg-white/[0.03] border border-white/10 rounded-xl p-5 print:bg-white print:border-gray-300"
    data-testid={testid}
  >
    <div className="flex items-center justify-between mb-3">
      <span className="text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3] print:text-gray-600">{label}</span>
      <div
        className="w-9 h-9 rounded-full flex items-center justify-center"
        style={{ backgroundColor: `${accent}1A` }}
      >
        <Icon size={16} style={{ color: accent }} />
      </div>
    </div>
    <div className="font-['Bebas_Neue'] text-3xl md:text-4xl text-white print:text-black leading-none">
      {value}
    </div>
    {sub && (
      <div className="mt-2 text-xs text-[#A3A3A3] print:text-gray-600">{sub}</div>
    )}
  </div>
);

const GrowthBadge = ({ pct }) => {
  if (pct == null) return null;
  const positive = pct >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  const color = positive ? '#22c55e' : '#ef4444';
  return (
    <span
      className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full"
      style={{ color, backgroundColor: `${color}1A` }}
    >
      <Icon size={12} />
      {fmtPct(pct)}
    </span>
  );
};

const TrendChart = ({ months }) => {
  // Lightweight inline SVG sparkline / bar combo — no chart deps needed.
  if (!months || months.length === 0) {
    return <div className="text-sm text-[#A3A3A3]">No trend data yet.</div>;
  }
  const maxRev = Math.max(1, ...months.map(m => m.revenue));
  const maxVis = Math.max(1, ...months.map(m => m.visitors));
  return (
    <div className="grid grid-cols-6 gap-3 items-end h-48 mt-4">
      {months.map((m, i) => {
        const revH = Math.max(4, (m.revenue / maxRev) * 140);
        const visH = Math.max(4, (m.visitors / maxVis) * 140);
        return (
          <div key={i} className="flex flex-col items-center gap-2" data-testid={`trend-month-${i}`}>
            <div className="flex items-end gap-1 h-[140px]">
              <div
                className="w-3 rounded-t-sm bg-[#D4AF37]"
                style={{ height: `${revH}px` }}
                title={`Revenue: ${fmtMoney(m.revenue)}`}
              />
              <div
                className="w-3 rounded-t-sm bg-green-500/70 print:bg-green-700"
                style={{ height: `${visH}px` }}
                title={`Visitors: ${m.visitors}`}
              />
            </div>
            <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3] print:text-gray-700">{m.label}</div>
          </div>
        );
      })}
    </div>
  );
};

const TopContentList = ({ title, items, valueKey, valueLabel, icon: Icon }) => (
  <div className="bg-white/[0.03] border border-white/10 rounded-xl p-5 print:bg-white print:border-gray-300">
    <div className="flex items-center gap-2 mb-4">
      <Icon size={16} className="text-[#D4AF37]" />
      <h4 className="text-sm uppercase tracking-[0.22em] text-white print:text-black font-semibold">{title}</h4>
    </div>
    {(!items || items.length === 0) ? (
      <p className="text-sm text-[#A3A3A3]">No data yet.</p>
    ) : (
      <ol className="space-y-2">
        {items.map((it, idx) => (
          <li key={idx} className="flex items-center justify-between gap-3 text-sm">
            <span className="flex items-center gap-3 min-w-0">
              <span className="text-[#D4AF37] font-['Bebas_Neue'] text-lg w-5 text-center">{idx + 1}</span>
              <span className="truncate text-white print:text-black">{it.title || 'Untitled'}</span>
            </span>
            <span className="text-xs text-[#A3A3A3] print:text-gray-700 whitespace-nowrap">
              {fmtNumber(it[valueKey] || 0)} {valueLabel}
            </span>
          </li>
        ))}
      </ol>
    )}
  </div>
);

const InvestorReportTab = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const printRef = useRef(null);

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axiosAuth.get('/api/admin/analytics/report');
      setReport(data);
    } catch (e) {
      logger.error('Failed to load investor report', e);
      setError('Could not load report — make sure you are signed in as an admin.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="text-center py-20 text-[#A3A3A3]" data-testid="investor-report-loading">
        Loading performance report…
      </div>
    );
  }
  if (error || !report) {
    return (
      <div className="text-center py-20 text-red-400" data-testid="investor-report-error">
        {error || 'No data.'}
      </div>
    );
  }

  const { audience, community, revenue, trend, top_content, catalog, generated_at } = report;
  const reportDate = new Date(generated_at).toLocaleDateString(undefined, {
    year: 'numeric', month: 'long', day: 'numeric'
  });

  return (
    <div className="space-y-8" data-testid="investor-report">
      {/* Action bar — hidden on print */}
      <div className="flex items-center justify-between flex-wrap gap-3 print:hidden">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-1">Investor Report</p>
          <h2 className="font-['Bebas_Neue'] text-3xl md:text-4xl">PLATFORM PERFORMANCE</h2>
          <p className="text-sm text-[#A3A3A3] mt-1">
            Share with banks, loan officers, partners and clients. Click Export to print or save as PDF.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchReport}
            className="px-3 py-2 bg-[#1A1A1A] border border-white/10 text-sm uppercase tracking-wider hover:bg-[#262626] flex items-center gap-2"
            data-testid="investor-report-refresh"
          >
            <RefreshCw size={14} />
            Refresh
          </button>
          <button
            onClick={handlePrint}
            className="px-4 py-2 bg-[#D4AF37] text-black text-sm uppercase tracking-wider font-semibold hover:bg-[#E5C048] flex items-center gap-2"
            data-testid="investor-report-export"
          >
            <Printer size={14} />
            Export PDF
          </button>
        </div>
      </div>

      {/* Printable area */}
      <div ref={printRef} id="investor-report-printable" className="space-y-8 print:bg-white print:text-black">
        {/* Print-only header */}
        <div className="hidden print:block border-b border-gray-300 pb-4 mb-4">
          <h1 className="font-['Bebas_Neue'] text-4xl">IAMHETHATIS — Performance Report</h1>
          <p className="text-sm text-gray-700">Generated {reportDate}</p>
        </div>

        {/* Audience */}
        <section data-testid="report-audience">
          <h3 className="text-xs uppercase tracking-[0.22em] text-[#A3A3A3] print:text-gray-600 mb-3">Audience</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard
              icon={Activity}
              label="Live Now"
              value={fmtNumber(audience.live_now)}
              sub="Active viewers this minute"
              accent="#22c55e"
              testid="kpi-live-now"
            />
            <KpiCard
              icon={Users}
              label="Visitors / Week"
              value={fmtNumber(audience.week_unique_visitors)}
              sub={`${fmtNumber(audience.today_unique_visitors)} today`}
              testid="kpi-visitors-week"
            />
            <KpiCard
              icon={Users}
              label="Visitors / 30 Days"
              value={fmtNumber(audience.month_unique_visitors)}
              sub={`All-time: ${fmtNumber(audience.all_time_visitors)}`}
              testid="kpi-visitors-month"
            />
            <KpiCard
              icon={Eye}
              label="All-Time Page Views"
              value={fmtNumber(audience.all_time_page_views)}
              sub="Every page, every fan"
              testid="kpi-pageviews"
            />
          </div>
        </section>

        {/* Revenue */}
        <section data-testid="report-revenue">
          <h3 className="text-xs uppercase tracking-[0.22em] text-[#A3A3A3] print:text-gray-600 mb-3">Revenue</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard
              icon={DollarSign}
              label="All-Time Revenue"
              value={fmtMoney(revenue.all_time.total)}
              sub={`${fmtNumber(revenue.all_time.count)} paid transactions`}
              testid="kpi-rev-all"
            />
            <KpiCard
              icon={DollarSign}
              label="Last 30 Days"
              value={fmtMoney(revenue.last_30_days.total)}
              sub={<>
                <GrowthBadge pct={revenue.growth_pct_30d} /> <span className="ml-1">vs prior 30d</span>
              </>}
              testid="kpi-rev-30d"
            />
            <KpiCard
              icon={DollarSign}
              label="Last 7 Days"
              value={fmtMoney(revenue.last_7_days.total)}
              sub={`${fmtNumber(revenue.last_7_days.count)} transactions`}
              testid="kpi-rev-7d"
            />
            <KpiCard
              icon={Users}
              label="Community"
              value={fmtNumber(community.total_users)}
              sub={<>
                +{fmtNumber(community.new_users_30d)} new (30d) <GrowthBadge pct={community.new_users_growth_pct} />
              </>}
              testid="kpi-community"
            />
          </div>

          {/* Revenue breakdown by type */}
          {revenue.breakdown_by_type && revenue.breakdown_by_type.length > 0 && (
            <div className="mt-4 bg-white/[0.03] border border-white/10 rounded-xl p-5 print:bg-white print:border-gray-300">
              <h4 className="text-sm uppercase tracking-[0.22em] text-white print:text-black font-semibold mb-3">
                Revenue by Product
              </h4>
              <div className="space-y-2">
                {revenue.breakdown_by_type.map((row, i) => {
                  const total = revenue.all_time.total || 1;
                  const pct = Math.round((row.total / total) * 100);
                  return (
                    <div key={i} data-testid={`rev-row-${row.type}`}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="capitalize text-white print:text-black">{row.type}</span>
                        <span className="text-[#A3A3A3] print:text-gray-700">
                          {fmtMoney(row.total)} · {fmtNumber(row.count)} sales · {pct}%
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full bg-white/5 print:bg-gray-200 overflow-hidden">
                        <div
                          className="h-full bg-[#D4AF37]"
                          style={{ width: `${Math.max(2, pct)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>

        {/* 6-month trend */}
        <section data-testid="report-trend">
          <div className="bg-white/[0.03] border border-white/10 rounded-xl p-5 print:bg-white print:border-gray-300">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="text-sm uppercase tracking-[0.22em] text-white print:text-black font-semibold">
                6-Month Growth
              </h3>
              <div className="flex items-center gap-4 text-xs text-[#A3A3A3] print:text-gray-700">
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-3 h-3 bg-[#D4AF37] rounded-sm" /> Revenue
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-3 h-3 bg-green-500/70 print:bg-green-700 rounded-sm" /> Visitors
                </span>
              </div>
            </div>
            <TrendChart months={trend.months} />
          </div>
        </section>

        {/* Top content */}
        <section data-testid="report-top-content">
          <h3 className="text-xs uppercase tracking-[0.22em] text-[#A3A3A3] print:text-gray-600 mb-3">Top Content</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <TopContentList
              title="Top Tracks"
              items={top_content.tracks}
              valueKey="play_count"
              valueLabel="plays"
              icon={Music}
            />
            <TopContentList
              title="Top Beats"
              items={top_content.beats}
              valueKey="sales_count"
              valueLabel="sales"
              icon={Radio}
            />
            <TopContentList
              title="Top Books / PDFs"
              items={top_content.books}
              valueKey="download_count"
              valueLabel="downloads"
              icon={BookOpen}
            />
          </div>
        </section>

        {/* Catalog footprint */}
        <section data-testid="report-catalog">
          <h3 className="text-xs uppercase tracking-[0.22em] text-[#A3A3A3] print:text-gray-600 mb-3">Catalog Footprint</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { k: 'tracks', label: 'Tracks' },
              { k: 'beats', label: 'Beats' },
              { k: 'books', label: 'Books' },
              { k: 'videos', label: 'Videos' },
              { k: 'products', label: 'Merch' },
            ].map(({ k, label }) => (
              <div
                key={k}
                className="bg-white/[0.03] border border-white/10 rounded-xl p-4 text-center print:bg-white print:border-gray-300"
                data-testid={`catalog-${k}`}
              >
                <div className="font-['Bebas_Neue'] text-3xl text-white print:text-black">{fmtNumber(catalog[k])}</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3] print:text-gray-600 mt-1">{label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Footer for printed page */}
        <div className="text-center pt-6 border-t border-white/10 print:border-gray-300">
          <p className="text-xs text-[#A3A3A3] print:text-gray-600">
            Generated by IAMHETHATIS Platform · {reportDate}
          </p>
          <p className="text-[10px] text-[#A3A3A3] print:text-gray-500 mt-1">
            iamhethatis.com — Young Black Man Inc.
          </p>
        </div>
      </div>
    </div>
  );
};

export default InvestorReportTab;
